import { useEffect } from 'react';

interface MetaTag {
  property?: string;
  name?: string;
  content: string;
}

interface DocumentMetaOptions {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
  structuredData?: Record<string, unknown>;
}

function setMetaTag(attr: 'property' | 'name', key: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.content = content;
}

function removeMetaTag(attr: 'property' | 'name', key: string) {
  const el = document.querySelector(`meta[${attr}="${key}"]`);
  if (el) el.remove();
}

const STRUCTURED_DATA_ID = 'structured-data-ld-json';

/**
 * Lightweight hook for managing document <title>, <meta>, OG tags, and structured data.
 * Cleans up on unmount, restoring defaults.
 */
export function useDocumentMeta(options: DocumentMetaOptions) {
  useEffect(() => {
    const prevTitle = document.title;
    // `description` is the one meta tag that exists in index.html before any
    // route runs, so it has to be RESTORED on cleanup rather than removed the
    // way the og:/twitter: tags are — otherwise leaving a page that set one
    // leaves its description behind on every page after it. null = there was
    // no tag to begin with, so cleanup removes ours.
    const prevDescription =
      document.querySelector('meta[name="description"]')?.getAttribute('content') ?? null;

    if (options.title) {
      document.title = options.title;
    }

    if (options.description) {
      setMetaTag('name', 'description', options.description);
    }

    // Open Graph
    if (options.ogTitle || options.title) {
      setMetaTag('property', 'og:title', options.ogTitle || options.title || '');
    }
    if (options.ogDescription || options.description) {
      setMetaTag('property', 'og:description', options.ogDescription || options.description || '');
    }
    if (options.ogImage) {
      setMetaTag('property', 'og:image', options.ogImage);
    }
    if (options.ogType) {
      setMetaTag('property', 'og:type', options.ogType);
    }

    // Twitter Card
    setMetaTag('name', 'twitter:card', options.twitterCard || 'summary_large_image');
    if (options.ogTitle || options.title) {
      setMetaTag('name', 'twitter:title', options.ogTitle || options.title || '');
    }
    if (options.ogDescription || options.description) {
      setMetaTag('name', 'twitter:description', options.ogDescription || options.description || '');
    }
    if (options.ogImage) {
      setMetaTag('name', 'twitter:image', options.ogImage);
    }

    // Canonical URL
    let canonicalEl: HTMLLinkElement | null = null;
    if (options.canonicalUrl) {
      canonicalEl = document.querySelector('link[rel="canonical"]');
      if (!canonicalEl) {
        canonicalEl = document.createElement('link');
        canonicalEl.rel = 'canonical';
        document.head.appendChild(canonicalEl);
      }
      canonicalEl.href = options.canonicalUrl;
    }

    // Structured Data (JSON-LD)
    let scriptEl: HTMLScriptElement | null = null;
    if (options.structuredData) {
      scriptEl = document.getElementById(STRUCTURED_DATA_ID) as HTMLScriptElement | null;
      if (!scriptEl) {
        scriptEl = document.createElement('script');
        scriptEl.id = STRUCTURED_DATA_ID;
        scriptEl.type = 'application/ld+json';
        document.head.appendChild(scriptEl);
      }
      scriptEl.textContent = JSON.stringify(options.structuredData);
    }

    // Cleanup on unmount
    return () => {
      document.title = prevTitle;
      if (options.description) {
        if (prevDescription === null) removeMetaTag('name', 'description');
        else setMetaTag('name', 'description', prevDescription);
      }
      removeMetaTag('property', 'og:title');
      removeMetaTag('property', 'og:description');
      removeMetaTag('property', 'og:image');
      removeMetaTag('property', 'og:type');
      removeMetaTag('name', 'twitter:card');
      removeMetaTag('name', 'twitter:title');
      removeMetaTag('name', 'twitter:description');
      removeMetaTag('name', 'twitter:image');
      if (canonicalEl) canonicalEl.remove();
      const sd = document.getElementById(STRUCTURED_DATA_ID);
      if (sd) sd.remove();
    };
  }, [
    options.title,
    options.description,
    options.canonicalUrl,
    options.ogTitle,
    options.ogDescription,
    options.ogImage,
    options.ogType,
    options.twitterCard,
    options.structuredData,
  ]);
}
